// A signed number: +2, 0, −1 (with a real minus sign).
export const formatSigned = (value: number): string =>
    value > 0 ? `+${value}` : (value < 0 ? `−${Math.abs(value)}` : '0');

// Seconds as "mm:ss".
export const formatMinutes = (seconds: number): string => {
    const whole = Math.round(seconds);
    return `${String(Math.floor(whole / 60)).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`;
};

export type AgeKey = 'age_minutes' | 'age_hours' | 'age_days';

// How long ago something happened, as a translation key and a count: minutes
// within the hour, hours within the day, days after that.
export function ageOf(then: Date, now: Date): { key: AgeKey; n: number } {
    const minutes = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 60_000));
    if (minutes < 60) {
        return { key: 'age_minutes', n: minutes };
    }
    if (minutes < 24 * 60) {
        return { key: 'age_hours', n: Math.floor(minutes / 60) };
    }
    return { key: 'age_days', n: Math.floor(minutes / (24 * 60)) };
}

// A "YYYY-MM-DD" date for display: 15.11.2026 in Finnish, Nov 15, 2026 in
// English. Read as a calendar date, so the time zone can't move it to another
// day. Anything else is shown as it is.
export function formatIsoDate(value: string, language: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) {
        return value;
    }
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (date.getMonth() !== Number(match[2]) - 1) {
        return value;
    }
    return language === 'fi'
        ? `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`
        : new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
}
